import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import LoginPage from './pages/LoginPage';
import PassLogsPage from './pages/PassLogsPage';
import DisplayPage from './pages/DisplayPage';
import MaintenancePage from './pages/MaintenancePage';
import CustomersPage from './pages/CustomersPage';
import CustomerProfilePage from './pages/CustomerProfilePage';
import QRPassesPage from './pages/QRPassesPage';
import HomeRedirect from './components/HomeRedirect';
import NavBar from './components/NavBar';
import { DatabaseProvider } from './context/DatabaseContext';
import { ThemeProvider } from '@mui/material';
import theme from './theme/theme'

// Flag for development mode
const isDevMode = false;

// Component for private routes
const PrivateRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <div></div>; // Quick operation without connection, do not show anything
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user.role === 'display') return <Navigate to="/display" replace />;
  return children;
};

// Component for admin routes
const AdminRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <div></div>; // Quick operation without connection, do not show anything
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!(user.role === 'administrator')) return <Navigate to="/home" replace />;
  return children;
};

// Component for display routes
const DisplayRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div></div>; // Quick operation without connection, do not show anything
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return children;
};

// Component for public routes
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div></div>; // Quick operation without connection, do not show anything
  return !isAuthenticated ? children : <Navigate to="/home" replace />;
};

// Main content component
const AppContent = () => {
  const { user, isAuthenticated, loading } = useAuth();

  const [forceUpdateKey, setForceUpdateKey] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const currentPath = location.pathname;

  const handleDatabaseChange = async (database) => {

    if (currentPath.includes('/customers/')) {
      await navigate('/customers');
    }

    setForceUpdateKey(prev => prev + 1);
  };

  return (
    <DatabaseProvider>
      <ThemeProvider theme={theme}>
        {isAuthenticated && !loading && (
          <NavBar onDatabaseChange={handleDatabaseChange} />
        )}

        <div style={{ marginTop: '1px' }}>
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={<PublicRoute><LoginPage /></PublicRoute>}
            />

            {/* Private routes */}
            <Route
              path="/home"
              element={<PrivateRoute><PassLogsPage key={forceUpdateKey} /></PrivateRoute>}
            />
            <Route
              path="/customers"
              element={<PrivateRoute><CustomersPage key={forceUpdateKey} /></PrivateRoute>}
            />
            <Route
              path="/customers/:id/*"
              element={<PrivateRoute><CustomerProfilePage key={forceUpdateKey} /></PrivateRoute>}
            />
            <Route
              path="/qrpasses"
              element={<PrivateRoute><QRPassesPage key={forceUpdateKey} /></PrivateRoute>}
            />

            {/* Display route */}
            <Route
              path="/display"
              element={<DisplayRoute><DisplayPage key={forceUpdateKey} /></DisplayRoute>}
            />

            {/* Admin route */}
            <Route
              path="/maintenance/*"
              element={<AdminRoute><MaintenancePage /></AdminRoute>}
            />

            {/* Redirect from home page */}
            <Route path="/" element={<HomeRedirect />} />
          </Routes>
        </div>
      </ThemeProvider>
    </DatabaseProvider>
  );
};

// Main App component
const App = () => (
  <AuthProvider>
    <Router>
      <AppContent />
    </Router>
  </AuthProvider>
);

export default App;
